/**
 * WS4 Stress-Test content. Strings ported verbatim from the vanilla
 * source — do NOT paraphrase:
 *
 *   - Objective / description / how-to-use / FUD + adoption captions:
 *     `_legacy/xaes-content.js` `XAES_CONTENT.ws4.stressTest`
 *     (lines 97-109).
 *   - Card deck (id + text):
 *     `_legacy/ws4-session.js` `WS4_SESSION.stressTest.cards`
 *     (lines 98-115).
 *   - Spectrum zone ids + labels + accent tokens: derived from the
 *     vanilla modal layout in `_legacy/wd.html` lines 1119-1158
 *     (HIGH RESISTANCE / RESIST → CONCERN → OPEN → EMBRACE → HIGH
 *     ADOPTION). The four DXC accents map zone → token as:
 *       resist   → red   (rgba(255,80,80,*) in vanilla)
 *       concern  → gold  (rgba(--accent-gold-rgb,*))
 *       open     → sky   (rgba(--accent-sky-rgb,*))
 *       embrace  → green (rgba(--accent-green-rgb,*))
 *
 * Vanilla cards carry no per-card classifier — the swatch on each card
 * derives from the zone it currently sits in. The unplaced deck shows
 * a neutral swatch.
 */

/** Spectrum zone identifier — also doubles as the persisted key. */
export type StressZoneId = "resist" | "concern" | "open" | "embrace";

export type StressZone = {
  id: StressZoneId;
  label: string;
  /** DXC palette token name, e.g. "red" / "gold" / "sky" / "green". */
  accent: "red" | "gold" | "sky" | "green";
  /** Aligned text inside the four-column zone-label row. */
  align: "left" | "center" | "right";
};

export type StressCard = {
  id: string;
  text: string;
};

export const STRESS_TEST_TITLE = "Stress-Test Role Play";

export const STRESS_TEST_DESCRIPTION =
  "Map real fears, uncertainties and doubts (FUD) against adoption drivers. Drag each card to its position on the spectrum — from high resistance to high adoption — to reveal where your organisation truly sits.";

export const STRESS_TEST_HOW_TO_USE: readonly string[] = [
  "Read each card in the unplaced stack",
  "Drag it to its position on the HIGH → LOW spectrum",
  "Discuss as a team — does the placement feel right?",
  "Identify patterns — where is resistance clustering?",
];

export const STRESS_TEST_FUD_DEFINITION =
  "Fear, Uncertainty & Doubt drivers that slow adoption.";

export const STRESS_TEST_ADOPTION_DEFINITION =
  "Positive signals and catalysts that accelerate change.";

/**
 * Spectrum endpoints rendered above the gradient bar — vanilla
 * markup at `_legacy/wd.html` lines 1119-1123.
 */
export const STRESS_TEST_HIGH_LABEL = "HIGH RESISTANCE";
export const STRESS_TEST_LOW_LABEL = "HIGH ADOPTION";
export const STRESS_TEST_SPECTRUM_LABEL = "← FUD × ADOPTION SPECTRUM →";

export const STRESS_ZONES: readonly StressZone[] = [
  { id: "resist", label: "RESIST", accent: "red", align: "left" },
  { id: "concern", label: "CONCERN", accent: "gold", align: "center" },
  { id: "open", label: "OPEN", accent: "sky", align: "center" },
  { id: "embrace", label: "EMBRACE", accent: "green", align: "right" },
];

/**
 * The 14-card deck — strings copied verbatim from
 * `_legacy/ws4-session.js` lines 98-115.
 */
/**
 * RT-CANVAS — per-workshop stress-test deck. Only WS4 has an authored
 * FUD×Adoption deck today; the spectrum + zones are generic framing that
 * applies to any workshop, so an unauthored workshop renders the empty
 * state (no cards to place) while the board chrome stays intact.
 */
export function getStressTest(workshopId: string): readonly StressCard[] | null {
  return STRESS_TEST_BY_WORKSHOP[workshopId] ?? null;
}

export const STRESS_TEST_CARDS: readonly StressCard[] = [
  { id: "c1", text: "The agent will make decisions I can no longer trace or challenge." },
  { id: "c2", text: "My team will spend less time on work that drains them." },
  { id: "c3", text: "If the agent fails at 2am, I won't know until morning." },
  { id: "c4", text: "Customers will get faster, more consistent responses." },
  { id: "c5", text: "We could accidentally automate a process that has hidden exceptions." },
  { id: "c6", text: "Junior staff may never develop the skills we built our careers on." },
  { id: "c7", text: "Our compliance team has not signed off on any of this." },
  { id: "c8", text: "The cost of running agents at scale is still unclear." },
  { id: "c9", text: "We could redeploy people to work that actually needs human judgement." },
  { id: "c10", text: "The first time an agent makes a visible mistake, trust collapses." },
  { id: "c11", text: "We move faster than competitors still running on manual processes." },
  { id: "c12", text: "Nobody has clearly defined who is responsible when it goes wrong." },
  { id: "c13", text: "Our best people spend 40% of their time on tasks an agent could handle." },
  { id: "c14", text: "The data we feed the agent reflects biases we have not addressed." },
];

const WS1_STRESS_TEST_CARDS: readonly StressCard[] = [
  { id: "ws1-c1", text: "Executives will greenlight a program they don't fully understand yet." },
  { id: "ws1-c2", text: "If the pilot underdelivers, sponsorship evaporates before scale." },
  { id: "ws1-c3", text: "A clear success metric gives every downstream workshop a target to aim at." },
  { id: "ws1-c4", text: "Competing priorities mean this loses budget the moment a crisis hits." },
  { id: "ws1-c5", text: "Executive air cover lets teams move fast without re-litigating the mandate." },
  { id: "ws1-c6", text: "Nobody has agreed who owns the outcome if the program stalls." },
  { id: "ws1-c7", text: "A shared vision here means every workshop after this one has less friction." },
  { id: "ws1-c8", text: "Leaders may say yes in the room and quietly disengage afterward." },
];

const WS2_STRESS_TEST_CARDS: readonly StressCard[] = [
  { id: "ws2-c1", text: "We could pick a process that looks impressive but has no real pain." },
  { id: "ws2-c2", text: "A clean baseline means we can actually prove the improvement later." },
  { id: "ws2-c3", text: "The team closest to the process wasn't in the room when we selected it." },
  { id: "ws2-c4", text: "Picking a well-scoped process builds early confidence for harder ones later." },
  { id: "ws2-c5", text: "Our current data on this process is too messy to trust as a baseline." },
  { id: "ws2-c6", text: "A visible quick win here earns credibility for the rest of the program." },
  { id: "ws2-c7", text: "We might be measuring the wrong step and missing the real bottleneck." },
  { id: "ws2-c8", text: "Mapping the as-is process honestly surfaces exceptions nobody documented." },
];

const WS3_STRESS_TEST_CARDS: readonly StressCard[] = [
  { id: "ws3-c1", text: "Redesigning around AI could remove judgement calls that actually matter." },
  { id: "ws3-c2", text: "A well-designed to-be process frees people from the most repetitive steps." },
  { id: "ws3-c3", text: "We're designing the future state without the people who'll run it." },
  { id: "ws3-c4", text: "A tighter to-be flow means fewer handoffs and fewer places to drop the ball." },
  { id: "ws3-c5", text: "The new design assumes data quality we don't actually have yet." },
  { id: "ws3-c6", text: "Designing exceptions in from day one avoids a brittle happy-path-only flow." },
  { id: "ws3-c7", text: "Nobody has stress-tested the design against the worst-case volume day." },
  { id: "ws3-c8", text: "A shared to-be model gives every team the same picture of where we're headed." },
];

const WS5_STRESS_TEST_CARDS: readonly StressCard[] = [
  { id: "ws5-c1", text: "An agent acting without a clear audit trail is a compliance finding waiting to happen." },
  { id: "ws5-c2", text: "Strong guardrails here let the business move faster with confidence, not slower." },
  { id: "ws5-c3", text: "Risk and legal weren't consulted until the design was already locked in." },
  { id: "ws5-c4", text: "A documented risk register turns vague unease into something we can actually manage." },
  { id: "ws5-c5", text: "We don't yet know who is accountable when an agent's decision is wrong." },
  { id: "ws5-c6", text: "Clear escalation paths mean incidents get caught before they become headlines." },
  { id: "ws5-c7", text: "Regulatory requirements could change faster than our governance model does." },
  { id: "ws5-c8", text: "A safety-by-design approach here builds trust with regulators, not just auditors." },
];

const WS6_STRESS_TEST_CARDS: readonly StressCard[] = [
  { id: "ws6-c1", text: "A demo that goes wrong in front of leadership can set the program back months." },
  { id: "ws6-c2", text: "A working blueprint demo turns skeptics into sponsors faster than any deck." },
  { id: "ws6-c3", text: "We're demoing the happy path and hoping nobody asks about the exceptions." },
  { id: "ws6-c4", text: "Seeing it work end-to-end makes the abstract roadmap suddenly feel real." },
  { id: "ws6-c5", text: "The Phase 0 blueprint may not hold up once real production data hits it." },
  { id: "ws6-c6", text: "A concrete demo gives budget holders something tangible to fund." },
  { id: "ws6-c7", text: "Stakeholders could mistake a polished demo for a finished, production-ready system." },
  { id: "ws6-c8", text: "Live feedback from this demo shapes the blueprint before it's too costly to change." },
];

const WS7_STRESS_TEST_CARDS: readonly StressCard[] = [
  { id: "ws7-c1", text: "A showcase that overpromises sets an expectation delivery can't match." },
  { id: "ws7-c2", text: "A strong executive showcase can unlock the next round of investment." },
  { id: "ws7-c3", text: "Some executives in the room still weren't part of the earlier workshops." },
  { id: "ws7-c4", text: "Seeing peers endorse this in the room is more persuasive than any business case." },
  { id: "ws7-c5", text: "The showcase highlights the win but glosses over what still needs to be solved." },
  { id: "ws7-c6", text: "A clear, honest showcase builds durable trust instead of a one-time wow." },
  { id: "ws7-c7", text: "Competing initiatives could be pitched harder in the same funding cycle." },
  { id: "ws7-c8", text: "This is the moment the program either gets sponsorship to scale or stalls." },
];

const WS8_STRESS_TEST_CARDS: readonly StressCard[] = [
  { id: "ws8-c1", text: "A flight plan without named owners quietly becomes shelfware." },
  { id: "ws8-c2", text: "A concrete flight plan turns momentum from this program into next year's budget." },
  { id: "ws8-c3", text: "We haven't agreed what happens to this team once the workshops end." },
  { id: "ws8-c4", text: "Sequencing the next 90 days now stops the post-workshop energy from fading." },
  { id: "ws8-c5", text: "Nobody has flagged which dependencies could slip the whole roadmap." },
  { id: "ws8-c6", text: "A shared flight plan gives every stakeholder the same view of what's next." },
  { id: "ws8-c7", text: "Funding for the next phase isn't secured — only assumed." },
  { id: "ws8-c8", text: "Naming a single accountable owner per milestone is what actually makes this stick." },
];

/** RT-CANVAS — per-workshop card decks. WS4 keeps its authored deck
 *  (back-compat); every other workshop now has its own themed FUD ×
 *  Adoption deck so the Stress-Test activity is usable from any
 *  workshop reached via the WorkshopNavigator. */
const STRESS_TEST_BY_WORKSHOP: Partial<Record<string, readonly StressCard[]>> = {
  WS1: WS1_STRESS_TEST_CARDS,
  WS2: WS2_STRESS_TEST_CARDS,
  WS3: WS3_STRESS_TEST_CARDS,
  WS4: STRESS_TEST_CARDS,
  WS5: WS5_STRESS_TEST_CARDS,
  WS6: WS6_STRESS_TEST_CARDS,
  WS7: WS7_STRESS_TEST_CARDS,
  WS8: WS8_STRESS_TEST_CARDS,
};
